import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Convierte el descanso escrito en el plan a segundos.
 * "90 s" -> 90 · "2 min" -> 120 · "2–3 min" -> 120 (toma el extremo bajo).
 */
export function parseRest(rest) {
  if (!rest) return 90
  const match = String(rest).match(/(\d+)/)
  if (!match) return 90
  const value = parseInt(match[1], 10)
  return /min/i.test(rest) ? value * 60 : value
}

export function formatClock(seconds) {
  const s = Math.max(0, seconds)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Pitido corto sin archivos externos, para no depender de assets. */
function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 880
    gain.gain.setValueAtTime(0.001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5)
    osc.start()
    osc.stop(ctx.currentTime + 0.52)
    osc.onended = () => ctx.close()
  } catch (e) {
    // Sin audio disponible: el temporizador sigue siendo visual.
  }
}

/** Temporizador de descanso entre series. */
export function useRestTimer() {
  const [timer, setTimer] = useState(null) // { label, remaining, total, running }
  const finishedRef = useRef(false)

  const start = useCallback((seconds, label) => {
    finishedRef.current = false
    setTimer({ label, total: seconds, remaining: seconds, running: true })
  }, [])

  const stop = useCallback(() => setTimer(null), [])

  const toggle = useCallback(
    () => setTimer((t) => (t ? { ...t, running: !t.running } : t)),
    []
  )

  const add = useCallback(
    (seconds) =>
      setTimer((t) => (t ? { ...t, remaining: t.remaining + seconds, running: true } : t)),
    []
  )

  useEffect(() => {
    if (!timer || !timer.running) return undefined
    const id = setInterval(() => {
      setTimer((t) => {
        if (!t || !t.running) return t
        const remaining = t.remaining - 1
        if (remaining <= 0) {
          if (!finishedRef.current) {
            finishedRef.current = true
            beep()
            if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(200)
          }
          return { ...t, remaining: 0, running: false }
        }
        return { ...t, remaining }
      })
    }, 1000)
    return () => clearInterval(id)
  }, [timer])

  return { timer, start, stop, toggle, add }
}
